import { X, Keyboard } from "lucide-react";
import { motion } from "framer-motion";
import { useChatPanel } from "./ChatPanelContext";
import { AudineroIcon } from "./AudineroIcon";
import type { ChatPanelHeaderProps } from "./types";
import { useTranslation } from "@/packages/utils";

export function ChatPanelHeader({ title, onClose }: ChatPanelHeaderProps) {
  const { t } = useTranslation("chat");
  const { closePanel } = useChatPanel();
  const handleClose = onClose ?? closePanel;

  return (
    <div className="relative flex items-center gap-3 px-5 py-4">
      {/* Ambient brand orb behind icon */}
      <div className="relative">
        <motion.div
          className="absolute -inset-1 rounded-full bg-accent/20 blur-sm"
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
        />
        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-accent/90 to-accent text-white shadow-sm">
          <AudineroIcon size={16} />
        </div>
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-[13px] font-semibold tracking-tight text-primary leading-tight">
          {title}
        </span>
        <div className="flex items-center gap-1.5 mt-0.5">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 ai-pulse-dot" />
          <span className="text-[10px] text-muted font-medium uppercase tracking-wider">
            {t("assistant.online")}
          </span>
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <div className="hidden sm:flex items-center gap-1 mr-1">
        <kbd className="inline-flex items-center gap-0.5 rounded-md border border-border/60 bg-page/60 px-1.5 py-0.5 text-[10px] font-medium text-muted">
          <Keyboard size={9} className="opacity-60" />
          <span>J</span>
        </kbd>
      </div>

      <button
        type="button"
        onClick={handleClose}
        className="h-7 w-7 rounded-full hover:bg-black/5 flex items-center justify-center text-muted hover:text-primary transition-all duration-200 cursor-pointer"
        aria-label={t("assistant.closeLabel")}
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}
